import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import type { Database } from '../lib/supabase';

type Product = Database['public']['Tables']['products']['Row'] & {
  // Transform database fields to match existing interface
  isFeatured: boolean;
  features: string[];
  price?: number | null;
  videoUrl?: string | null;
  createdAt: string;
  updatedAt: string;
  relatedPageSlugs: string[];
};

type Page = Database['public']['Tables']['pages']['Row'] & {
  // Transform database fields to match existing interface
  metaTitle: string;
  metaDescription: string;
  createdAt: string;
  updatedAt: string;
};

type BlogPost = Database['public']['Tables']['blog_posts']['Row'] & {
  // Transform database fields to match existing interface
  seoTitle: string;
  seoDescription: string;
  focusKeyword: string;
  canonicalUrl: string;
  featuredImage: string;
  authorId: string;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
};

type Plugin = Database['public']['Tables']['plugins']['Row'] & {
  // Transform database fields to match existing interface
  isActive: boolean;
  isInstalled: boolean;
};

type Theme = Database['public']['Tables']['themes']['Row'] & {
  // Transform database fields to match existing interface
  isActive: boolean;
};

type Inquiry = Database['public']['Tables']['inquiries']['Row'] & {
  createdAt: string;
};

type Lead = Database['public']['Tables']['leads']['Row'] & {
  createdAt: string;
};

type VideoFile = Database['public']['Tables']['video_library']['Row'] & {
  // Transform database fields to match existing interface
  uploadedAt: string;
};

type ImageFile = Database['public']['Tables']['image_library']['Row'] & {
  // Transform database fields to match existing interface
  uploadedAt: string;
};

// Settings interface
interface SiteSettings {
  general: {
    siteTitle: string;
    tagline: string;
    siteUrl: string;
    adminEmail: string;
    timezone: string;
    language: string;
    favicon: string;
    logo: string;
  };
  contact: {
    phone: string;
    email: string;
    address: string;
    businessHours: string;
  };
  social: {
    facebook: string;
    twitter: string;
    instagram: string;
    linkedin: string;
    youtube: string;
    whatsapp: string;
  };
  appearance: {
    primaryColor: string;
    secondaryColor: string;
    fontFamily: string;
    headerStyle: string;
    footerStyle: string;
    showBreadcrumbs: boolean;
    showSearchBar: boolean;
    productCardSize: 'small' | 'medium' | 'large';
  };
  seo: {
    defaultMetaTitle: string;
    defaultMetaDescription: string;
    ogImage: string;
    twitterHandle: string;
    googleAnalyticsId: string;
    googleTagManagerId: string;
    facebookPixelId: string;
  };
}

interface DataContextType {
  products: Product[];
  pages: Page[];
  blogPosts: BlogPost[];
  plugins: Plugin[];
  themes: Theme[];
  inquiries: Inquiry[];
  leads: Lead[];
  videoLibrary: VideoFile[];
  imageLibrary: ImageFile[];
  settings: SiteSettings;
  loading: boolean;
  blogPostsLoading: boolean;
  blogPostsHasMore: boolean;
  blogPostsTotal: number;
  addProduct: (product: Omit<Product, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateProduct: (id: string, product: Partial<Product>) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  addPage: (page: Omit<Page, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updatePage: (id: string, page: Partial<Page>) => Promise<void>;
  deletePage: (id: string) => Promise<void>;
  addBlogPost: (post: Omit<BlogPost, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateBlogPost: (id: string, post: Partial<BlogPost>) => Promise<void>;
  deleteBlogPost: (id: string) => Promise<void>;
  togglePlugin: (id: string) => Promise<void>;
  activateTheme: (id: string) => Promise<void>;
  updateInquiryStatus: (id: string, status: Inquiry['status']) => Promise<void>;
  addInquiry: (inquiry: Omit<Inquiry, 'id' | 'created_at'>) => Promise<void>;
  addLead: (lead: Omit<Lead, 'id' | 'created_at'>) => Promise<void>;
  updateLeadStatus: (id: string, status: Lead['status']) => Promise<void>;
  uploadVideo: (file: File) => Promise<string>;
  getVideoLibrary: () => Promise<void>;
  deleteVideo: (id: string) => Promise<void>;
  uploadImage: (file: File) => Promise<string>;
  getImageLibrary: () => Promise<void>;
  deleteImage: (id: string) => Promise<void>;
  loadSettings: () => Promise<void>;
  updateSettings: (key: string, value: any) => Promise<void>;
  initializeDefaultSettings: () => Promise<void>;
  forceUpdateContactEmail: () => Promise<void>;
  loadPublishedBlogPosts: (page?: number, limit?: number, reset?: boolean) => Promise<{ posts: BlogPost[]; hasMore: boolean; total: number }>;
  loadMoreBlogPosts: () => Promise<void>;
  searchBlogPosts: (query: string, page?: number, limit?: number) => Promise<{ posts: BlogPost[]; hasMore: boolean; total: number }>;
  filterBlogPostsByCategory: (category: string, page?: number, limit?: number) => Promise<{ posts: BlogPost[]; hasMore: boolean; total: number }>;
  clearBlogPostsCache: () => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [videoLibrary, setVideoLibrary] = useState<VideoFile[]>([]);
  const [imageLibrary, setImageLibrary] = useState<ImageFile[]>([]);
  
  // Blog optimization states
  const [blogPostsCache, setBlogPostsCache] = useState<Map<string, BlogPost[]>>(new Map());
  const [blogPostsLoading, setBlogPostsLoading] = useState(false);
  const [blogPostsHasMore, setBlogPostsHasMore] = useState(true);
  const [blogPostsPage, setBlogPostsPage] = useState(1);
  const [blogPostsTotal, setBlogPostsTotal] = useState(0);
  
  const [settings, setSettings] = useState<SiteSettings>({
    general: {
      siteTitle: '',
      tagline: '',
      siteUrl: '',
      adminEmail: 'simranindustries143@gmail.com',
      timezone: '',
      language: '',
      favicon: '',
      logo: '',
    },
    contact: {
      phone: '',
      email: '',
      address: '',
      businessHours: '',
    },
    social: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
      youtube: '',
      whatsapp: '',
    },
    appearance: {
      primaryColor: '',
      secondaryColor: '',
      fontFamily: '',
      headerStyle: '',
      footerStyle: '',
      showBreadcrumbs: false,
      showSearchBar: false,
      productCardSize: 'medium',
    },
    seo: {
      defaultMetaTitle: '',
      defaultMetaDescription: '',
      ogImage: '',
      twitterHandle: '',
      googleAnalyticsId: '',
      googleTagManagerId: '',
      facebookPixelId: '',
    },
  });
  const [loading, setLoading] = useState(true);

  // Utility function to generate slug from title
  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Transform database row to match existing interface
  const transformProduct = (row: Database['public']['Tables']['products']['Row']): Product => ({
    ...row,
    isFeatured: row.is_featured || false,
    features: row.features || [],
    price: row.price || null,
    videoUrl: row.videoUrl || null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    related_page_slugs: row.related_page_slugs || [],
    relatedPageSlugs: row.related_page_slugs || []
  });

  const transformPage = (row: Database['public']['Tables']['pages']['Row']): Page => ({
    ...row,
    metaTitle: row.meta_title || '',
    metaDescription: row.meta_description || '',
    createdAt: row.created_at,
    updatedAt: row.updated_at
  });

  const transformBlogPost = (row: Database['public']['Tables']['blog_posts']['Row']): BlogPost => ({
    ...row,
    seoTitle: row.seo_title || '',
    seoDescription: row.seo_description || '',
    focusKeyword: row.focus_keyword || '',
    canonicalUrl: row.canonical_url || '',
    featuredImage: row.featured_image || '',
    authorId: row.author_id || '',
    publishedAt: row.published_at || '',
    createdAt: row.created_at,
    updatedAt: row.updated_at
  });

  const transformPlugin = (row: Database['public']['Tables']['plugins']['Row']): Plugin => ({
    ...row,
    isActive: row.is_active || false,
    isInstalled: row.is_installed || false
  });

  const transformTheme = (row: Database['public']['Tables']['themes']['Row']): Theme => ({
    ...row,
    isActive: row.is_active || false
  });

  const transformInquiry = (row: Database['public']['Tables']['inquiries']['Row']): Inquiry => ({
    ...row,
    createdAt: row.created_at
  });

  const transformLead = (row: Database['public']['Tables']['leads']['Row']): Lead => ({
    ...row,
    createdAt: row.created_at
  });

  const transformVideoFile = (row: Database['public']['Tables']['video_library']['Row']): VideoFile => ({
    ...row,
    uploadedAt: row.created_at
  });

  const transformImageFile = (row: Database['public']['Tables']['image_library']['Row']): ImageFile => ({
    ...row,
    uploadedAt: row.created_at
  });

  // Load initial data
  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        console.log('DataContext: Starting to load data...');
        if (mounted) {
          setLoading(true);
        }
        
        // Check Supabase configuration first
        if (isSupabaseConfigured()) {
          console.log('DataContext: Supabase is configured, attempting to load data...');
          try {
            // Test connection first with a simple query
            const { data: testData, error: testError } = await supabase
              .from('products')
              .select('count', { count: 'exact', head: true });
            
            if (testError) {
              console.error('DataContext: Supabase connection test failed:', testError);
              // Fall back to sample data if connection fails
              loadSampleData();
              return;
            }
            
            console.log('DataContext: Supabase connection test passed');
            
            // If connection test passes, load all data
            await Promise.all([
              loadProducts().catch(error => {
                console.error('DataContext: Error loading products:', error);
                // Continue with other data loads
              }),
              loadPages().catch(error => {
                console.error('DataContext: Error loading pages:', error);
                // Continue with other data loads
              }),
              loadBlogPosts().catch(error => {
                console.error('DataContext: Error loading blog posts:', error);
                // Continue with other data loads
              }),
              loadPlugins().catch(error => {
                console.error('DataContext: Error loading plugins:', error);
                // Continue with other data loads
              }),
              loadThemes().catch(error => {
                console.error('DataContext: Error loading themes:', error);
                // Continue with other data loads
              }),
              loadInquiries().catch(error => {
                console.error('DataContext: Error loading inquiries:', error);
                // Continue with other data loads
              }),
              loadLeads().catch(error => {
                console.error('DataContext: Error loading leads:', error);
                // Continue with other data loads
              }),
              loadSettings().catch(error => {
                console.error('DataContext: Error loading settings:', error);
                // Continue with other data loads
              }),
              getImageLibrary().catch(error => {
                console.error('DataContext: Error loading image library:', error);
                // Continue with other data loads
              })
            ]);
            console.log('DataContext: All data loaded successfully');
          } catch (error) {
            console.error('DataContext: Error loading data from Supabase:', error);
            // Fall back to sample data if Supabase fails
            loadSampleData();
          }
        } else {
          console.log('DataContext: Supabase not configured, loading sample data...');
          // Load sample data if Supabase is not configured
          loadSampleData();
        }
      } catch (error) {
        console.error('DataContext: Error in loadData:', error);
        // Always fall back to sample data on any error
        loadSampleData();
      } finally {
        if (mounted) {
          console.log('DataContext: Setting loading to false');
          setLoading(false);
        }
      }
    };

    loadData();

    // Set up real-time subscriptions only if Supabase is properly configured
    let subscriptions: any[] = [];
    
    if (isSupabaseConfigured()) {
      try {
        const productsSubscription = supabase
          .channel('products_changes')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, () => {
            if (mounted) loadProducts().catch(console.error);
          })
          .subscribe();

        const pagesSubscription = supabase
          .channel('pages_changes')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'pages' }, () => {
            if (mounted) loadPages().catch(console.error);
          })
          .subscribe();

        const blogPostsSubscription = supabase
          .channel('blog_posts_changes')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'blog_posts' }, () => {
            if (mounted) loadBlogPosts().catch(console.error);
          })
          .subscribe();

        const inquiriesSubscription = supabase
          .channel('inquiries_changes')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'inquiries' }, () => {
            if (mounted) loadInquiries().catch(console.error);
          })
          .subscribe();

        const leadsSubscription = supabase
          .channel('leads_changes')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'leads' }, () => {
            if (mounted) loadLeads().catch(console.error);
          })
          .subscribe();

        const settingsSubscription = supabase
          .channel('settings_changes')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'site_settings' }, () => {
            if (mounted) loadSettings().catch(console.error);
          })
          .subscribe();

        subscriptions = [productsSubscription, pagesSubscription, blogPostsSubscription, inquiriesSubscription, leadsSubscription, settingsSubscription];
      } catch (error) {
        console.error('Error setting up subscriptions:', error);
      }
    }

    return () => {
      mounted = false;
      subscriptions.forEach(sub => {
        try {
          sub?.unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing:', error);
        }
      });
    };
  }, []);

  const loadSampleData = () => {
    console.log('DataContext: Loading sample data...');
    // Load sample data for demo purposes
    setProducts([
      {
        id: '1',
        name: 'Cotton Seed Cake',
        category: 'Feed',
        description: 'High-protein cattle feed made from premium cotton seeds. Rich in essential nutrients for enhanced milk production.',
        image: 'https://images.pexels.com/photos/2733918/pexels-photo-2733918.jpeg',
        videoUrl: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4',
        features: ['High Protein Content (20-25%)', 'Rich in Essential Amino Acids', 'Improves Milk Quality', 'Cost-Effective Solution'],
        isFeatured: true,
        status: 'published',
        is_featured: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        price: 45,
        related_page_slugs: ['benefits-cotton-seed-cake-dairy-cattle'],
        relatedPageSlugs: ['benefits-cotton-seed-cake-dairy-cattle']
      },
      {
        id: '2',
        name: 'Soybean Meal',
        category: 'Feed',
        description: 'Premium soybean meal with high protein content. Ideal for dairy cattle and livestock nutrition.',
        image: 'https://images.pexels.com/photos/1459505/pexels-photo-1459505.jpeg',
        videoUrl: null,
        features: ['High Protein (44-48%)', 'Balanced Amino Acid Profile', 'Digestible Energy', 'Cost-Effective'],
        isFeatured: false,
        status: 'published',
        is_featured: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        price: 52,
        related_page_slugs: [],
        relatedPageSlugs: []
      }
    ]);

    setPages([
      {
        id: '1',
        title: 'About Us',
        slug: 'about-us',
        content: '<h1>About Simran Industries</h1><p>We are a leading provider of high-quality cattle feed and agricultural products.</p>',
        meta_title: 'About Us - Simran Industries',
        meta_description: 'Learn about Simran Industries, a leading provider of high-quality cattle feed and agricultural products.',
        status: 'published',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        metaTitle: 'About Us - Simran Industries',
        metaDescription: 'Learn about Simran Industries, a leading provider of high-quality cattle feed and agricultural products.',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ]);

    // Add sample blog posts
    console.log('DataContext: Setting sample blog posts...');
    setBlogPosts([
      {
        id: '1',
        title: 'Benefits of Cotton Seed Cake for Dairy Cattle',
        slug: 'benefits-cotton-seed-cake-dairy-cattle',
        content: '<h1>Benefits of Cotton Seed Cake for Dairy Cattle</h1><p>Cotton seed cake is an excellent source of protein for dairy cattle, providing essential nutrients that enhance milk production and overall animal health.</p><h2>Nutritional Value</h2><p>Cotton seed cake contains 20-25% protein, making it an ideal supplement for dairy cattle. The high protein content helps improve milk yield and quality.</p><h2>Key Benefits</h2><ul><li>High protein content (20-25%)</li><li>Improves milk production</li><li>Cost-effective feeding solution</li><li>Enhances animal health</li><li>Easy to digest</li></ul><h2>Feeding Guidelines</h2><ol><li>Start with small quantities</li><li>Gradually increase the amount</li><li>Monitor animal response</li><li>Adjust based on milk yield</li><li>Maintain consistent feeding schedule</li></ol><h2>Cost-Effective Solution</h2><p>Compared to other protein sources, cotton seed cake offers excellent value for money while maintaining high nutritional standards.</p>',
        excerpt: 'Learn about the nutritional benefits of cotton seed cake for improving milk production and cattle health.',
        featured_image: 'https://images.pexels.com/photos/2733918/pexels-photo-2733918.jpeg',
        author_id: '',
        categories: ['Nutrition', 'Dairy'],
        tags: ['cotton seed cake', 'dairy cattle', 'nutrition'],
        seo_title: 'Benefits of Cotton Seed Cake for Dairy Cattle - Simran Industries',
        seo_description: 'Discover how cotton seed cake can improve milk production and cattle health. Learn about nutritional benefits and cost-effective feeding solutions.',
        focus_keyword: 'cotton seed cake',
        canonical_url: '',
        status: 'published',
        published_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        seoTitle: 'Benefits of Cotton Seed Cake for Dairy Cattle - Simran Industries',
        seoDescription: 'Discover how cotton seed cake can improve milk production and cattle health. Learn about nutritional benefits and cost-effective feeding solutions.',
        focusKeyword: 'cotton seed cake',
        canonicalUrl: '',
        featuredImage: 'https://images.pexels.com/photos/2733918/pexels-photo-2733918.jpeg',
        authorId: '',
        publishedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: '2',
        title: 'Test Blog Post with Lists',
        slug: 'test-blog-post-with-lists',
        content: '<h1>Test Blog Post with Lists</h1><p>This is a test blog post to verify that our list spacing changes are working correctly.</p><h2>Unordered List Test</h2><ul><li>First item in the list</li><li>Second item with more text to see how it wraps</li><li>Third item</li><li>Fourth item with even more text to test line wrapping and spacing</li><li>Fifth and final item</li></ul><h2>Ordered List Test</h2><ol><li>Step one in the process</li><li>Step two with additional details</li><li>Step three</li><li>Step four with more information</li><li>Final step</li></ol><h2>Nested List Test</h2><ul><li>Main item one<ul><li>Sub-item 1.1</li><li>Sub-item 1.2</li><li>Sub-item 1.3</li></ul></li><li>Main item two<ol><li>Sub-step 2.1</li><li>Sub-step 2.2</li></ol></li><li>Main item three</li></ul><p>This should demonstrate that our list spacing optimizations are working properly.</p>',
        excerpt: 'A test blog post to verify list spacing improvements.',
        featured_image: 'https://images.pexels.com/photos/2733918/pexels-photo-2733918.jpeg',
        author_id: '',
        categories: ['Test'],
        tags: ['test', 'lists', 'spacing'],
        seo_title: 'Test Blog Post with Lists - Simran Industries',
        seo_description: 'A test blog post to verify list spacing improvements.',
        focus_keyword: 'test lists',
        canonical_url: '',
        status: 'published',
        published_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        seoTitle: 'Test Blog Post with Lists - Simran Industries',
        seoDescription: 'A test blog post to verify list spacing improvements.',
        focusKeyword: 'test lists',
        canonicalUrl: '',
        featuredImage: 'https://images.pexels.com/photos/2733918/pexels-photo-2733918.jpeg',
        authorId: '',
        publishedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ]);
    console.log('DataContext: Sample blog posts set successfully');

    setPlugins([]);
    setThemes([]);
    setInquiries([]);
    setLeads([]);
    
    // Initialize default settings
    setSettings({
      general: {
        siteTitle: 'Simran Industries',
        tagline: 'Premium Cotton Seed Cake & Cattle Feed Manufacturer',
        siteUrl: 'https://simranindustries.com',
        adminEmail: 'simranindustries143@gmail.com',
        timezone: 'Asia/Kolkata',
        language: 'en',
        favicon: '/favicon.ico',
        logo: '/logo.png',
      },
      contact: {
        phone: '+91-9977724699',
        email: 'simranindustries143@gmail.com',
        address: '318/26, Bholaram Compound, Palda, Indore, MP - 452001',
        businessHours: 'Mon - Sat: 9:00 AM - 6:00 PM',
      },
      social: {
        facebook: '',
        twitter: '',
        instagram: '',
        linkedin: '',
        youtube: '',
        whatsapp: '+91-9977724699',
      },
      appearance: {
        primaryColor: '#d97706',
        secondaryColor: '#f59e0b',
        fontFamily: 'Inter',
        headerStyle: 'fixed',
        footerStyle: 'default',
        showBreadcrumbs: true,
        showSearchBar: true,
        productCardSize: 'medium',
      },
      seo: {
        defaultMetaTitle: 'Simran Industries - Premium Cotton Seed Cake & Cattle Feed',
        defaultMetaDescription: 'Leading manufacturer of premium cotton seed cake and cattle feed in India.',
        ogImage: '/og-image.jpg',
        twitterHandle: '@simranindustries',
        googleAnalyticsId: '',
        googleTagManagerId: '',
        facebookPixelId: '',
      },
    });
    
    console.log('DataContext: Sample data loading completed');
  };

  const loadProducts = async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, skipping products load');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading products:', error);
        // Don't throw error, just log it and continue
        return;
      }
      
      if (data) {
        setProducts(data.map(transformProduct));
      }
    } catch (error) {
      console.error('Error in loadProducts:', error);
      // Don't throw error, just log it and continue
    }
  };

  const loadPages = async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, skipping pages load');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading pages:', error);
        // Don't throw error, just log it and continue
        return;
      }
      
      if (data) {
        setPages(data.map(transformPage));
      }
    } catch (error) {
      console.error('Error in loadPages:', error);
      // Don't throw error, just log it and continue
    }
  };

  const loadBlogPosts = async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, skipping blog posts load');
      return;
    }
    
    try {
      console.log('DataContext: Loading blog posts...');
      // For admin panel, load all posts
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('DataContext: Error loading blog posts:', error);
        // Don't throw error, just log it and continue
        return;
      }
      
      console.log('DataContext: Blog posts loaded:', data?.length || 0, 'posts');
      if (data) {
        setBlogPosts(data.map(transformBlogPost));
      }
    } catch (error) {
      console.error('DataContext: Error in loadBlogPosts:', error);
      // Don't throw error, just log it and continue
    }
  };

  // Optimized blog loading for public pages
  const loadPublishedBlogPosts = async (page: number = 1, limit: number = 12, reset: boolean = false) => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, skipping blog posts load');
      return { posts: [], hasMore: false, total: 0 };
    }
    
    const cacheKey = `published_${page}_${limit}`;
    
    // Check cache first
    if (!reset && blogPostsCache.has(cacheKey)) {
      const cached = blogPostsCache.get(cacheKey);
      if (cached) {
        return { posts: cached, hasMore: blogPostsHasMore, total: blogPostsTotal };
      }
    }
    
    try {
      setBlogPostsLoading(true);
      
      // Get total count first
      const { count, error: countError } = await supabase
        .from('blog_posts')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'published');
      
      if (countError) {
        console.error('Error getting blog posts count:', countError);
        throw countError;
      }
      
      const total = count || 0;
      const offset = (page - 1) * limit;
      
      // Load published posts with pagination
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      
      if (error) {
        console.error('Error loading published blog posts:', error);
        throw error;
      }
      
      const posts = data ? data.map(transformBlogPost) : [];
      const hasMore = offset + limit < total;
      
      // Update cache
      setBlogPostsCache(prev => new Map(prev.set(cacheKey, posts)));
      setBlogPostsHasMore(hasMore);
      setBlogPostsTotal(total);
      
      return { posts, hasMore, total };
    } catch (error) {
      console.error('Error in loadPublishedBlogPosts:', error);
      throw error;
    } finally {
      setBlogPostsLoading(false);
    }
  };

  // Load more blog posts for infinite scroll
  const loadMoreBlogPosts = async () => {
    if (blogPostsLoading || !blogPostsHasMore) return;
    
    const nextPage = blogPostsPage + 1;
    setBlogPostsPage(nextPage);
    
    try {
      const { posts, hasMore } = await loadPublishedBlogPosts(nextPage, 18, false);
      
      // Append to existing posts
      setBlogPosts(prev => [...prev, ...posts]);
      setBlogPostsHasMore(hasMore);
    } catch (error) {
      console.error('Error loading more blog posts:', error);
      // Revert page number on error
      setBlogPostsPage(prev => prev - 1);
    }
  };

  // Search blog posts with pagination
  const searchBlogPosts = async (query: string, page: number = 1, limit: number = 12) => {
    if (!isSupabaseConfigured()) {
      return { posts: [], hasMore: false, total: 0 };
    }
    
    try {
      setBlogPostsLoading(true);
      
      const offset = (page - 1) * limit;
      
      // Search in title, excerpt, and content
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .or(`title.ilike.%${query}%,excerpt.ilike.%${query}%,content.ilike.%${query}%`)
        .order('published_at', { ascending: false })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      
      if (error) {
        console.error('Error searching blog posts:', error);
        throw error;
      }
      
      const posts = data ? data.map(transformBlogPost) : [];
      const hasMore = posts.length === limit;
      
      return { posts, hasMore, total: posts.length };
    } catch (error) {
      console.error('Error in searchBlogPosts:', error);
      throw error;
    } finally {
      setBlogPostsLoading(false);
    }
  };

  // Filter blog posts by category
  const filterBlogPostsByCategory = async (category: string, page: number = 1, limit: number = 12) => {
    if (!isSupabaseConfigured()) {
      return { posts: [], hasMore: false, total: 0 };
    }
    
    try {
      setBlogPostsLoading(true);
      
      const offset = (page - 1) * limit;
      
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .contains('categories', [category])
        .order('published_at', { ascending: false })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      
      if (error) {
        console.error('Error filtering blog posts by category:', error);
        throw error;
      }
      
      const posts = data ? data.map(transformBlogPost) : [];
      const hasMore = posts.length === limit;
      
      return { posts, hasMore, total: posts.length };
    } catch (error) {
      console.error('Error in filterBlogPostsByCategory:', error);
      throw error;
    } finally {
      setBlogPostsLoading(false);
    }
  };

  // Clear blog posts cache
  const clearBlogPostsCache = () => {
    setBlogPostsCache(new Map());
    setBlogPostsPage(1);
    setBlogPostsHasMore(true);
    setBlogPostsTotal(0);
  };

  const loadPlugins = async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, skipping plugins load');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('plugins')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading plugins:', error);
        // Don't throw error, just log it and continue
        return;
      }
      
      if (data) {
        setPlugins(data.map(transformPlugin));
      }
    } catch (error) {
      console.error('Error in loadPlugins:', error);
      // Don't throw error, just log it and continue
    }
  };

  const loadThemes = async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, skipping themes load');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('themes')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading themes:', error);
        // Don't throw error, just log it and continue
        return;
      }
      
      if (data) {
        setThemes(data.map(transformTheme));
      }
    } catch (error) {
      console.error('Error in loadThemes:', error);
      // Don't throw error, just log it and continue
    }
  };

  const loadInquiries = async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, skipping inquiries load');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('inquiries')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading inquiries:', error);
        // Don't throw error, just log it and continue
        return;
      }
      
      if (data) {
        setInquiries(data.map(transformInquiry));
      }
    } catch (error) {
      console.error('Error in loadInquiries:', error);
      // Don't throw error, just log it and continue
    }
  };

  const loadLeads = async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, skipping leads load');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading leads:', error);
        // Don't throw error, just log it and continue
        return;
      }
      
      if (data) {
        setLeads(data.map(transformLead));
      }
    } catch (error) {
      console.error('Error in loadLeads:', error);
      // Don't throw error, just log it and continue
    }
  };

  const addProduct = async (product: Omit<Product, 'id' | 'created_at' | 'updated_at'>) => {
    if (!isSupabaseConfigured()) {
      // For demo purposes, add to local state
      const newProduct = {
        ...product,
        id: Date.now().toString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        is_featured: product.isFeatured,
        related_page_slugs: product.relatedPageSlugs || []
      };
      setProducts(prev => [newProduct, ...prev]);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('products')
        .insert({
          name: product.name,
          category: product.category,
          description: product.description,
          image: product.image,
          videoUrl: product.videoUrl,
          price: product.price,
          features: product.features,
          is_featured: product.isFeatured,
          status: product.status,
          related_page_slugs: product.relatedPageSlugs || []
        })
        .select()
        .single();

      if (error) throw error;
      if (data) setProducts(prev => [transformProduct(data), ...prev]);
    } catch (error) {
      console.error('Error adding product:', error);
      throw error;
    }
  };

  const updateProduct = async (id: string, updates: Partial<Product>) => {
    console.log('updateProduct called with:', { id, updates }); // Debug log
    
    if (!isSupabaseConfigured()) {
      // For demo purposes, update local state
      console.log('Supabase not configured, updating local state'); // Debug log
      setProducts(prev => prev.map(p => p.id === id ? { 
        ...p, 
        ...updates, 
        updatedAt: new Date().toISOString(),
        is_featured: updates.isFeatured !== undefined ? updates.isFeatured : p.is_featured,
        related_page_slugs: updates.relatedPageSlugs !== undefined ? updates.relatedPageSlugs : p.related_page_slugs
      } : p));
      return;
    }
    
    try {
      const dbUpdates: Database['public']['Tables']['products']['Update'] = {};
      
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.category !== undefined) dbUpdates.category = updates.category;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.image !== undefined) dbUpdates.image = updates.image;
      if (updates.videoUrl !== undefined) {
        dbUpdates.videoUrl = updates.videoUrl;
        console.log('Setting videoUrl in database update:', updates.videoUrl); // Debug log
      }
      if (updates.price !== undefined) dbUpdates.price = updates.price;
      if (updates.features !== undefined) dbUpdates.features = updates.features;
      if (updates.isFeatured !== undefined) dbUpdates.is_featured = updates.isFeatured;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.relatedPageSlugs !== undefined) dbUpdates.related_page_slugs = updates.relatedPageSlugs;

      console.log('Database updates object:', dbUpdates); // Debug log

      const { data, error } = await supabase
        .from('products')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Database update error:', error); // Debug log
        throw error;
      }
      if (data) {
        console.log('Database update successful, data returned:', data); // Debug log
        setProducts(prev => prev.map(p => p.id === id ? transformProduct(data) : p));
      }
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  };

  const deleteProduct = async (id: string) => {
    if (!isSupabaseConfigured()) {
      // For demo purposes, remove from local state
      setProducts(prev => prev.filter(p => p.id !== id));
      return;
    }
    
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  };

  const addPage = async (page: Omit<Page, 'id' | 'created_at' | 'updated_at'>) => {
    if (!isSupabaseConfigured()) {
      // For demo purposes, add to local state
      const newPage = {
        ...page,
        id: Date.now().toString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        meta_title: page.metaTitle,
        meta_description: page.metaDescription
      };
      setPages(prev => [newPage, ...prev]);
      return;
    }
    
    try {
      // Auto-generate slug if not provided
      const slug = page.slug || generateSlug(page.title);
      
      const { data, error } = await supabase
        .from('pages')
        .insert({
          title: page.title,
          slug: slug,
          content: page.content,
          meta_title: page.metaTitle,
          meta_description: page.metaDescription,
          status: page.status
        })
        .select()
        .single();

      if (error) throw error;
      if (data) setPages(prev => [transformPage(data), ...prev]);
    } catch (error) {
      console.error('Error adding page:', error);
      throw error;
    }
  };

  const updatePage = async (id: string, updates: Partial<Page>) => {
    if (!isSupabaseConfigured()) {
      // For demo purposes, update local state
      setPages(prev => prev.map(p => p.id === id ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p));
      return;
    }
    
    try {
      const dbUpdates: Database['public']['Tables']['pages']['Update'] = {};
      
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.slug !== undefined) dbUpdates.slug = updates.slug;
      if (updates.content !== undefined) dbUpdates.content = updates.content;
      if (updates.metaTitle !== undefined) dbUpdates.meta_title = updates.metaTitle;
      if (updates.metaDescription !== undefined) dbUpdates.meta_description = updates.metaDescription;
      if (updates.status !== undefined) dbUpdates.status = updates.status;

      const { data, error } = await supabase
        .from('pages')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setPages(prev => prev.map(p => p.id === id ? transformPage(data) : p));
      }
    } catch (error) {
      console.error('Error updating page:', error);
      throw error;
    }
  };

  const deletePage = async (id: string) => {
    if (!isSupabaseConfigured()) {
      // For demo purposes, remove from local state
      setPages(prev => prev.filter(p => p.id !== id));
      return;
    }
    
    try {
      const { error } = await supabase
        .from('pages')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setPages(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      console.error('Error deleting page:', error);
      throw error;
    }
  };

  const addBlogPost = async (post: Omit<BlogPost, 'id' | 'created_at' | 'updated_at'>) => {
    if (!isSupabaseConfigured()) {
      // For demo purposes, add to local state
      const newPost = {
        ...post,
        id: Date.now().toString(),
        slug: post.slug || generateSlug(post.title),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        seo_title: post.seoTitle,
        seo_description: post.seoDescription,
        focus_keyword: post.focusKeyword,
        canonical_url: post.canonicalUrl,
        featured_image: post.featuredImage,
        author_id: post.authorId,
        published_at: post.publishedAt
      };
      setBlogPosts(prev => [newPost, ...prev]);
      // Clear cache when new post is added
      clearBlogPostsCache();
      return;
    }
    
    try {
      // Auto-generate slug if not provided
      const slug = post.slug || generateSlug(post.title);
      
      const insertData: any = {
        title: post.title,
        slug: slug,
        content: post.content,
        excerpt: post.excerpt,
        featured_image: post.featuredImage,
        categories: post.categories,
        tags: post.tags,
        seo_title: post.seoTitle,
        seo_description: post.seoDescription,
        focus_keyword: post.focusKeyword,
        canonical_url: post.canonicalUrl,
        status: post.status,
        published_at: post.status === 'published' ? new Date().toISOString() : null
      };
      if (post.authorId && post.authorId.trim() !== '') {
        insertData.author_id = post.authorId;
      }
      const { data, error } = await supabase
        .from('blog_posts')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;
      if (data) setBlogPosts(prev => [transformBlogPost(data), ...prev]);
      
      // Clear cache when new post is added
      clearBlogPostsCache();
    } catch (error) {
      console.error('Error adding blog post:', error);
      throw error;
    }
  };

  const updateBlogPost = async (id: string, updates: Partial<BlogPost>) => {
    console.log('updateBlogPost called with:', { id, updates }); // Debug log
    
    if (!isSupabaseConfigured()) {
      // For demo purposes, update local state
      setBlogPosts(prev => prev.map(post => 
        post.id === id 
          ? { ...post, ...updates, updated_at: new Date().toISOString(), updatedAt: new Date().toISOString() }
          : post
      ));
      // Clear cache when post is updated
      clearBlogPostsCache();
      return;
    }
    
    try {
      const updateData: any = {};
      
      // Map frontend field names to database field names
      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.slug !== undefined) updateData.slug = updates.slug;
      if (updates.content !== undefined) updateData.content = updates.content;
      if (updates.excerpt !== undefined) updateData.excerpt = updates.excerpt;
      if (updates.featuredImage !== undefined) updateData.featured_image = updates.featuredImage;
      if (updates.categories !== undefined) updateData.categories = updates.categories;
      if (updates.tags !== undefined) updateData.tags = updates.tags;
      if (updates.seoTitle !== undefined) updateData.seo_title = updates.seoTitle;
      if (updates.seoDescription !== undefined) updateData.seo_description = updates.seoDescription;
      if (updates.focusKeyword !== undefined) updateData.focus_keyword = updates.focusKeyword;
      if (updates.canonicalUrl !== undefined) updateData.canonical_url = updates.canonicalUrl;
      if (updates.status !== undefined) {
        updateData.status = updates.status;
        updateData.published_at = updates.status === 'published' ? new Date().toISOString() : null;
      }
      if (updates.authorId !== undefined) updateData.author_id = updates.authorId;
      
      updateData.updated_at = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('blog_posts')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setBlogPosts(prev => prev.map(post => 
          post.id === id ? transformBlogPost(data) : post
        ));
      }
      
      // Clear cache when post is updated
      clearBlogPostsCache();
    } catch (error) {
      console.error('Error updating blog post:', error);
      throw error;
    }
  };

  const deleteBlogPost = async (id: string) => {
    if (!isSupabaseConfigured()) {
      // For demo purposes, remove from local state
      setBlogPosts(prev => prev.filter(post => post.id !== id));
      // Clear cache when post is deleted
      clearBlogPostsCache();
      return;
    }
    
    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setBlogPosts(prev => prev.filter(post => post.id !== id));
      
      // Clear cache when post is deleted
      clearBlogPostsCache();
    } catch (error) {
      console.error('Error deleting blog post:', error);
      throw error;
    }
  };

  const togglePlugin = async (id: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Database not configured');
    }
    
    try {
      const plugin = plugins.find(p => p.id === id);
      if (!plugin) return;

      const { data, error } = await supabase
        .from('plugins')
        .update({ is_active: !plugin.isActive })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setPlugins(prev => prev.map(p => p.id === id ? transformPlugin(data) : p));
      }
    } catch (error) {
      console.error('Error toggling plugin:', error);
      throw error;
    }
  };

  const activateTheme = async (id: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Database not configured');
    }
    
    try {
      // First deactivate all themes
      await supabase
        .from('themes')
        .update({ is_active: false })
        .neq('id', id);

      // Then activate the selected theme
      const { data, error } = await supabase
        .from('themes')
        .update({ is_active: true })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      // Reload all themes to reflect changes
      await loadThemes();
    } catch (error) {
      console.error('Error activating theme:', error);
      throw error;
    }
  };

  const updateInquiryStatus = async (id: string, status: Inquiry['status']) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Database not configured');
    }
    
    try {
      const { data, error } = await supabase
        .from('inquiries')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setInquiries(prev => prev.map(i => i.id === id ? transformInquiry(data) : i));
      }
    } catch (error) {
      console.error('Error updating inquiry status:', error);
      throw error;
    }
  };

  const addInquiry = async (inquiry: Omit<Inquiry, 'id' | 'created_at'>) => {
    if (!isSupabaseConfigured()) {
      // For demo purposes, add to local state
      const newInquiry = {
        ...inquiry,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        created_at: new Date().toISOString()
      };
      setInquiries(prev => [newInquiry, ...prev]);
      return;
    }
    
    try {
      console.log('Adding inquiry to database:', inquiry);
      
      const insertData = {
        name: inquiry.name,
        email: inquiry.email,
        phone: inquiry.phone,
        company: inquiry.company || null,
        product: inquiry.product || null,
        quantity: inquiry.quantity || null,
        location: inquiry.location || null,
        message: inquiry.message,
        status: inquiry.status || 'new'
      };

      console.log('Insert data prepared:', insertData);

      const { data, error } = await supabase
        .from('inquiries')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Database insert error:', error);
        throw error;
      }

      if (data) {
        console.log('Inquiry added successfully:', data);
        setInquiries(prev => [transformInquiry(data), ...prev]);
      } else {
        throw new Error('No data returned from insert operation');
      }
    } catch (error) {
      console.error('Error adding inquiry:', error);
      throw error;
    }
  };

  const addLead = async (lead: Omit<Lead, 'id' | 'created_at'>) => {
    if (!isSupabaseConfigured()) {
      // For demo purposes, add to local state
      const newLead = {
        ...lead,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        created_at: new Date().toISOString()
      };
      setLeads(prev => [newLead, ...prev]);
      return;
    }
    
    try {
      console.log('Adding lead to database:', lead);
      
      const insertData = {
        mobile: lead.mobile,
        product: lead.product || null,
        quantity: lead.quantity || null,
        status: lead.status || 'new',
        source: lead.source || 'quote_button'
      };

      console.log('Insert data prepared:', insertData);

      const { data, error } = await supabase
        .from('leads')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Database insert error:', error);
        throw error;
      }

      if (data) {
        console.log('Lead added successfully:', data);
        setLeads(prev => [transformLead(data), ...prev]);
      } else {
        throw new Error('No data returned from insert operation');
      }
    } catch (error) {
      console.error('Error adding lead:', error);
      throw error;
    }
  };

  const updateLeadStatus = async (id: string, status: Lead['status']) => {
    if (!isSupabaseConfigured()) {
      // For demo purposes, update local state
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l));
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('leads')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setLeads(prev => prev.map(l => l.id === id ? transformLead(data) : l));
      }
    } catch (error) {
      console.error('Error updating lead status:', error);
      throw error;
    }
  };

  const uploadVideo = async (file: File) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Database not configured');
    }
    
    try {
      console.log('Starting video upload:', { fileName: file.name, fileSize: file.size, fileType: file.type });
      
      // Upload file to Supabase Storage
      const fileName = `${Date.now()}-${file.name}`;
      console.log('Uploading to storage bucket:', fileName);
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('videos')
        .upload(fileName, file);

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw new Error(`Storage upload failed: ${uploadError.message}`);
      }

      console.log('File uploaded successfully:', uploadData);

      // Get public URL for the uploaded file
      const { data: urlData } = supabase.storage
        .from('videos')
        .getPublicUrl(fileName);

      console.log('Public URL generated:', urlData.publicUrl);

      // Save video metadata to database
      const { data, error } = await supabase
        .from('video_library')
        .insert({
          name: file.name,
          url: urlData.publicUrl,
          size: file.size,
          file_type: file.type,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Database insert error:', error);
        throw new Error(`Database insert failed: ${error.message}`);
      }
      
      if (data) {
        console.log('Video metadata saved:', data);
        setVideoLibrary(prev => [transformVideoFile(data), ...prev]);
        return data.url;
      }
      
      throw new Error('Failed to save video metadata - no data returned');
    } catch (error) {
      console.error('Error uploading video:', error);
      throw error;
    }
  };

  const getVideoLibrary = async () => {
    if (!isSupabaseConfigured()) {
      throw new Error('Database not configured');
    }
    
    try {
      const { data, error } = await supabase
        .from('video_library')
        .select('*');

      if (error) throw error;
      if (data) {
        setVideoLibrary(data.map(transformVideoFile));
      }
    } catch (error) {
      console.error('Error getting video library:', error);
      throw error;
    }
  };

  const deleteVideo = async (id: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Database not configured');
    }
    
    try {
      const { error } = await supabase
        .from('video_library')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setVideoLibrary(prev => prev.filter(v => v.id !== id));
    } catch (error) {
      console.error('Error deleting video:', error);
      throw error;
    }
  };

  const uploadImage = async (file: File) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Database not configured');
    }
    
    try {
      console.log('Starting image upload:', { fileName: file.name, fileSize: file.size, fileType: file.type });
      
      // Get image dimensions if it's an image file
      let width: number | undefined;
      let height: number | undefined;
      
      if (file.type.startsWith('image/')) {
        try {
          const dimensions = await getImageDimensions(file);
          width = dimensions.width;
          height = dimensions.height;
        } catch (error) {
          console.warn('Could not get image dimensions:', error);
        }
      }
      
      // Upload file to Supabase Storage
      const fileName = `${Date.now()}-${file.name}`;
      console.log('Uploading to storage bucket:', fileName);
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('images')
        .upload(fileName, file);

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw new Error(`Storage upload failed: ${uploadError.message}`);
      }

      console.log('File uploaded successfully:', uploadData);

      // Get public URL for the uploaded file
      const { data: urlData } = supabase.storage
        .from('images')
        .getPublicUrl(fileName);

      console.log('Public URL generated:', urlData.publicUrl);

      // Save image metadata to database
      const { data, error } = await supabase
        .from('image_library')
        .insert({
          name: file.name,
          url: urlData.publicUrl,
          size: file.size,
          file_type: file.type,
          width: width,
          height: height,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Database insert error:', error);
        throw new Error(`Database insert failed: ${error.message}`);
      }
      
      if (data) {
        console.log('Image metadata saved:', data);
        setImageLibrary(prev => [transformImageFile(data), ...prev]);
        return data.url;
      }
      
      throw new Error('Failed to save image metadata - no data returned');
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  // Helper function to get image dimensions
  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const getImageLibrary = async () => {
    if (!isSupabaseConfigured()) {
      throw new Error('Database not configured');
    }
    
    try {
      const { data, error } = await supabase
        .from('image_library')
        .select('*');

      if (error) throw error;
      if (data) {
        setImageLibrary(data.map(transformImageFile));
      }
    } catch (error) {
      console.error('Error getting image library:', error);
      throw error;
    }
  };

  const deleteImage = async (id: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Database not configured');
    }
    
    try {
      const { error } = await supabase
        .from('image_library')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setImageLibrary(prev => prev.filter(i => i.id !== id));
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  };

  const initializeDefaultSettings = async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot initialize settings');
      return;
    }
    
    try {
      console.log('Initializing default settings...');
      
      // First, check which settings already exist
      const { data: existingSettings, error: checkError } = await supabase
        .from('site_settings')
        .select('key')
        .in('key', ['general', 'contact', 'social', 'appearance', 'seo']);
      
      if (checkError) {
        console.error('Error checking existing settings:', checkError);
        return;
      }
      
      const existingKeys = existingSettings?.map(s => s.key) || [];
      console.log('Existing settings keys:', existingKeys);
      
      const defaultSettings = [
        {
          key: 'general',
          value: {
            siteTitle: 'Simran Industries',
            tagline: 'Premium Cotton Seed Cake & Cattle Feed Manufacturer',
            siteUrl: 'https://simranindustries.com',
            adminEmail: 'simranindustries143@gmail.com',
            timezone: 'Asia/Kolkata',
            language: 'en',
            favicon: '/favicon.ico',
            logo: '/logo.png'
          }
        },
        {
          key: 'contact',
          value: {
            phone: '+91-9977724699',
            email: 'simranindustries143@gmail.com',
            address: '318/26, Bholaram Compound, Palda, Indore, MP - 452001',
            businessHours: 'Mon - Sat: 9:00 AM - 6:00 PM'
          }
        },
        {
          key: 'social',
          value: {
            facebook: '',
            twitter: '',
            instagram: '',
            linkedin: '',
            youtube: '',
            whatsapp: '+91-9977724699'
          }
        },
        {
          key: 'appearance',
          value: {
            primaryColor: '#d97706',
            secondaryColor: '#f59e0b',
            fontFamily: 'Inter',
            headerStyle: 'fixed',
            footerStyle: 'default',
            showBreadcrumbs: true,
            showSearchBar: true,
            productCardSize: 'medium'
          }
        },
        {
          key: 'seo',
          value: {
            defaultMetaTitle: 'Simran Industries - Premium Cotton Seed Cake & Cattle Feed',
            defaultMetaDescription: 'Leading manufacturer of premium cotton seed cake and cattle feed in India.',
            ogImage: '/og-image.jpg',
            twitterHandle: '@simranindustries',
            googleAnalyticsId: '',
            googleTagManagerId: '',
            facebookPixelId: ''
          }
        }
      ];

      // Only upsert settings that don't already exist
      const settingsToUpsert = defaultSettings.filter(setting => !existingKeys.includes(setting.key));
      
      if (settingsToUpsert.length === 0) {
        console.log('All default settings already exist, skipping initialization');
        return;
      }
      
      console.log('Settings to upsert:', settingsToUpsert.map(s => s.key));

      // Use upsert to avoid conflicts - this will insert if not exists, update if exists
      for (const setting of settingsToUpsert) {
        try {
          const { error } = await supabase
            .from('site_settings')
            .upsert({
              key: setting.key,
              value: setting.value,
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'key' // Use the 'key' field as conflict target since it's unique
            });

          if (error) {
            console.error(`Error upserting setting ${setting.key}:`, error);
          } else {
            console.log(`Setting ${setting.key} upserted successfully`);
          }
        } catch (error) {
          console.error(`Error upserting setting ${setting.key}:`, error);
        }
      }

      console.log('Default settings initialization completed');
    } catch (error) {
      console.error('Error initializing default settings:', error);
      // Don't throw error, just log it and continue
    }
  };

  const forceUpdateContactEmail = async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot update contact email');
      return;
    }
    
    try {
      console.log('Force updating contact email...');
      
      // Update contact settings with correct email
      const { error: contactError } = await supabase
        .from('site_settings')
        .upsert({
          key: 'contact',
          value: {
            phone: '+91-9977724699',
            email: 'simranindustries143@gmail.com',
            address: '318/26, Bholaram Compound, Palda, Indore, MP - 452001',
            businessHours: 'Mon - Sat: 9:00 AM - 6:00 PM'
          },
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key' // Use the 'key' field as conflict target since it's unique
        });

      if (contactError) {
        console.error('Error updating contact settings:', contactError);
      } else {
        console.log('Contact email updated successfully');
      }

      // Update general settings with correct admin email
      const { error: generalError } = await supabase
        .from('site_settings')
        .upsert({
          key: 'general',
          value: {
            siteTitle: 'Simran Industries',
            tagline: 'Premium Cotton Seed Cake & Cattle Feed Manufacturer',
            siteUrl: 'https://simranindustries.com',
            adminEmail: 'simranindustries143@gmail.com',
            timezone: 'Asia/Kolkata',
            language: 'en',
            favicon: '/favicon.ico',
            logo: '/logo.png'
          },
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key' // Use the 'key' field as conflict target since it's unique
        });

      if (generalError) {
        console.error('Error updating general settings:', generalError);
      } else {
        console.log('General settings updated successfully');
      }
    } catch (error) {
      console.error('Error force updating contact email:', error);
    }
  };

  const loadSettings = async () => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, using default settings');
      return;
    }
    
    try {
      console.log('Loading settings from database...');
      const { data, error } = await supabase
        .from('site_settings')
        .select('*');

      if (error) {
        console.error('Error loading settings:', error);
        // Don't throw error, just log it and continue
        return;
      }
      
      console.log('Settings data from database:', data);
      
      if (data && data.length > 0) {
        // Convert array of settings to object
        const settingsObject: any = {};
        data.forEach(setting => {
          settingsObject[setting.key] = setting.value;
        });
        
        console.log('Processed settings object:', settingsObject);
        
        // Merge with default settings
        const mergedSettings: SiteSettings = {
          general: {
            siteTitle: settingsObject.general?.siteTitle || 'Simran Industries',
            tagline: settingsObject.general?.tagline || 'Premium Cotton Seed Cake & Cattle Feed Manufacturer',
            siteUrl: settingsObject.general?.siteUrl || 'https://simranindustries.com',
            adminEmail: settingsObject.general?.adminEmail || 'simranindustries143@gmail.com',
            timezone: settingsObject.general?.timezone || 'Asia/Kolkata',
            language: settingsObject.general?.language || 'en',
            favicon: settingsObject.general?.favicon || '/favicon.ico',
            logo: settingsObject.general?.logo || '/logo.png',
          },
          contact: {
            phone: settingsObject.contact?.phone || '+91-9977724699',
            email: settingsObject.contact?.email || 'simranindustries143@gmail.com',
            address: settingsObject.contact?.address || '318/26, Bholaram Compound, Palda, Indore, MP - 452001',
            businessHours: settingsObject.contact?.businessHours || 'Mon - Sat: 9:00 AM - 6:00 PM',
          },
          social: {
            facebook: settingsObject.social?.facebook || '',
            twitter: settingsObject.social?.twitter || '',
            instagram: settingsObject.social?.instagram || '',
            linkedin: settingsObject.social?.linkedin || '',
            youtube: settingsObject.social?.youtube || '',
            whatsapp: settingsObject.social?.whatsapp || '+91-9977724699',
          },
          appearance: {
            primaryColor: settingsObject.appearance?.primaryColor || '',
            secondaryColor: settingsObject.appearance?.secondaryColor || '',
            fontFamily: settingsObject.appearance?.fontFamily || '',
            headerStyle: settingsObject.appearance?.headerStyle || '',
            footerStyle: settingsObject.appearance?.footerStyle || '',
            showBreadcrumbs: settingsObject.appearance?.showBreadcrumbs || false,
            showSearchBar: settingsObject.appearance?.showSearchBar || false,
            productCardSize: settingsObject.appearance?.productCardSize || 'medium',
          },
          seo: {
            defaultMetaTitle: settingsObject.seo?.defaultMetaTitle || 'Simran Industries - Premium Cotton Seed Cake & Cattle Feed',
            defaultMetaDescription: settingsObject.seo?.defaultMetaDescription || 'Leading manufacturer of premium cotton seed cake and cattle feed in India.',
            ogImage: settingsObject.seo?.ogImage || '/og-image.jpg',
            twitterHandle: settingsObject.seo?.twitterHandle || '@simranindustries',
            googleAnalyticsId: settingsObject.seo?.googleAnalyticsId || '',
            googleTagManagerId: settingsObject.seo?.googleTagManagerId || '',
            facebookPixelId: settingsObject.seo?.facebookPixelId || '',
          },
        };
        
        console.log('Final merged settings:', mergedSettings);
        setSettings(mergedSettings);
      } else {
        console.log('No settings found in database, using defaults');
      }
    } catch (error) {
      console.error('Error in loadSettings:', error);
      // Don't throw error, just log it and continue
    }
  };

  const updateSettings = async (key: string, value: any) => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, cannot update settings');
      return;
    }

    try {
      const { error } = await supabase
        .from('site_settings')
        .upsert(
          {
            key: key,
            value: value,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'key',
          }
        );

      if (error) {
        console.error(`Error upserting setting ${key}:`, error);
        throw error;
      }

      setSettings((prev) => ({
        ...prev,
        [key]: value,
      }));
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  };

  return (
    <DataContext.Provider value={{
      products,
      pages,
      blogPosts,
      plugins,
      themes,
      inquiries,
      leads,
      videoLibrary,
      imageLibrary,
      settings,
      loading,
      blogPostsLoading,
      blogPostsHasMore,
      blogPostsTotal,
      addProduct,
      updateProduct,
      deleteProduct,
      addPage,
      updatePage,
      deletePage,
      addBlogPost,
      updateBlogPost,
      deleteBlogPost,
      togglePlugin,
      activateTheme,
      updateInquiryStatus,
      addInquiry,
      addLead,
      updateLeadStatus,
      uploadVideo,
      getVideoLibrary,
      deleteVideo,
      uploadImage,
      getImageLibrary,
      deleteImage,
      loadSettings,
      updateSettings,
      initializeDefaultSettings,
      forceUpdateContactEmail,
      loadPublishedBlogPosts,
      loadMoreBlogPosts,
      searchBlogPosts,
      filterBlogPostsByCategory,
      clearBlogPostsCache
    }}>
      {children}
    </DataContext.Provider>
  );
};